import React, { useContext, useEffect, useState } from 'react';
import client from './ApiClient';

interface FlagInfo {
    value: string | undefined;
    is_active: boolean;
}

export enum FeatureFlag {
    AUTOSCHEDULE_WORKER = 'AUTOSCHEDULE_WORKER',
    CENTER_CHATWOOT_ENABLED = 'CENTER_CHATWOOT_ENABLED',
    FAST_QUALIFICATION_FILTER = 'FAST_QUALIFICATION_FILTER',
    FLORIDA_CLEARINGHOUSE_MANUAL_CHECK = 'FLORIDA_CLEARINGHOUSE_MANUAL_CHECK',
    RECRUIT = 'RECRUIT',
    RECRUIT_CANDIDATE = 'RECRUIT_CANDIDATE',
    RECRUIT_SCORECARDS = 'RECRUIT_SCORECARDS',
    RECRUIT_OFFERS = 'RECRUIT_OFFERS',
}

type FeatureFlagMap = {
    [key in FeatureFlag]?: FlagInfo;
};

type FeatureFlagContextType = {
    flags: FeatureFlagMap;
    loaded: boolean;
};

interface FeatureFlagResponse extends FlagInfo {
    category: keyof FeatureFlag;
}

const FeatureFlagContext = React.createContext<FeatureFlagContextType>({ flags: {}, loaded: false });

const FIVE_MINUTES = 1000 * 60 * 5;

export function FeatureFlagProvider({ children }: { children: React.ReactNode }) {
    const [flags, setFlags] = useState<FeatureFlagMap>();
    const [refreshInterval, setRefreshInterval] = useState<number>(0);

    const fetchData = () => {
        client('api/feature-flag/')
            .then((res: FeatureFlagResponse[]) => {
                const newFlags = res.reduce((a, flag) => {
                    const { category, ...other } = flag;
                    return { ...a, [category]: other };
                }, {});
                setFlags(newFlags);
            })
            .catch(() => {});
    };

    useEffect(() => {
        if (refreshInterval) {
            const interval = setInterval(fetchData, refreshInterval);
            return () => clearInterval(interval);
        } else {
            fetchData();
            setRefreshInterval(FIVE_MINUTES);
        }
    }, [refreshInterval]);

    return (
        <FeatureFlagContext.Provider value={{ flags: flags ?? {}, loaded: flags !== undefined }}>
            {children}
        </FeatureFlagContext.Provider>
    );
}

export function useFeatureFlag(key: FeatureFlag) {
    const { flags, loaded } = useContext(FeatureFlagContext);

    return {
        featureActive: flags[`${key}`]?.is_active ?? false,
        featureValue: flags[`${key}`]?.value,
        featuresLoaded: loaded,
    };
}
